import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit2,
  MoreHorizontal, 
  XCircle,
  Loader2,
  BookOpen,
  ArrowUpRight,
  ArrowDownLeft,
  Archive,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Calendar,
  User,
  Clock,
  History,
  Download,
  FileDown,
  Upload,
  PlusSquare,
  ExternalLink,
  Link2,
  Check
} from 'lucide-react';
import { notify } from '../services/notify';
import { 
  db, 
  auth, 
  collection, 
  query, 
  orderBy, 
  where,
  onSnapshot, 
  addDoc, 
  setDoc,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  getDocs,
  handleFirestoreError,
  OperationType
} from '../firebase';

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useExclusiveDropdown } from '../lib/dropdowns';
import { normalizeCurrency } from '../lib/currency';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const exportToCSV = (data: any[], filename: string) => {
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => headers.map(header => {
      const val = row[header];
      return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(','))
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

interface Account {
  id: string;
  code: string;
  name: string;
  type: 
    | 'Bank' | 'Current Asset' | 'Fixed Asset' | 'Non-current Asset'
    | 'Equity'
    | 'Direct Cost' | 'Expense'
    | 'Other Income' | 'Sales'
    | 'Current Liability' | 'Non-current Liability';
  nature: 'Debit' | 'Credit';
  isSystemAccount?: boolean;
  enablePayments?: boolean;
  showOnDashboardWatchlist?: boolean;
  showInExpenseClaims?: boolean;
  description?: string;
  taxRate?: string;
  ytd?: number;
  status: 'active' | 'archived' | 'deleted';
  lastActionBy?: string;
  lastActionAt?: any;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
  createdAt?: any;
  actionLog?: {
    type: 'create' | 'archive' | 'delete' | 'restore' | 'update';
    by: string;
    at: any;
  }[];
}

const ACCOUNT_TYPES = {
  Assets: ['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset'],
  Equity: ['Equity'],
  Expenses: ['Direct Cost', 'Expense'],
  Revenue: ['Other Income', 'Sales'],
  Liability: ['Current Liability', 'Non-current Liability']
};

const getLocalAuthToken = () =>
  typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;

const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = getLocalAuthToken();
  if (!token) throw new Error('Local session token is unavailable');

  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    throw new Error((await response.text()) || `Request failed (${response.status})`);
  }

  return response.status === 204 ? (undefined as T) : response.json();
};

const localApiAccounts = (payload: any): Account[] =>
  Array.isArray(payload) ? payload : payload?.accounts || payload?.chartOfAccounts || payload?.data || [];

const localApiAccount = (payload: any): Account =>
  (payload?.account || payload?.data || payload) as Account;

const normalizeLocalSettings = (payload: any) => {
  if (!payload) return null;
  return {
    ...payload,
    currency: normalizeCurrency(payload.currency ?? payload.defaultCurrency ?? payload.default_currency),
    defaultAccounts: payload.defaultAccounts ?? payload.default_accounts ?? {},
  };
};

import { CORE_CHART_OF_ACCOUNTS } from '../constants';
import Papa from 'papaparse';

export default function ChartOfAccountsView({ orgId , language = 'en', onNavigate } : { language?: string,  orgId: string, onNavigate?: (tab: string, id?: string | null, extra?: any) => void }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const announceDropdownOpen = useExclusiveDropdown('chart-of-accounts', () => setActiveMenu(null));
  const [settings, setSettings] = useState<any>(null);
  const [basicSeeded, setBasicSeeded] = useState(false);
  const [isAccountixImportOpen, setIsAccountixImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<Record<string, string>[]>([]);
  const [importHeaders, setImportHeaders] = useState<string[]>([]);
  const [importMapping, setImportMapping] = useState<Record<string, string>>({});
  const [importFileName, setImportFileName] = useState('');

  const accountixTemplateRows = [{
    'Account Code': '1000',
    'Account Name': 'Business Bank Account',
    'Account Type': 'BANK',
    'Tax Code': 'TAX002',
    Description: 'Main business bank account',
    'Enable Payments To Account': 'TRUE',
    'Show In Expense Claims': 'FALSE',
    Status: 'ACTIVE',
    'System Account': '',
    'Reporting Code': '',
    'Reporting Code Name': '',
  }, {
    'Account Code': '4000',
    'Account Name': 'Sales',
    'Account Type': 'REVENUE',
    'Tax Code': 'TAX001',
    Description: 'Sales revenue',
    'Enable Payments To Account': 'FALSE',
    'Show In Expense Claims': 'FALSE',
    Status: 'ACTIVE',
    'System Account': '',
    'Reporting Code': '',
    'Reporting Code Name': '',
  }];

  const accountixTemplateUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(Papa.unparse(accountixTemplateRows))}`;
  const accountixFieldAliases: Record<string, string[]> = {
    code: ['code', 'account code', 'accountcode'],
    name: ['name', 'account name', 'accountname'],
    type: ['type', 'account type', 'accounttype'],
    nature: ['nature', 'debit/credit', 'balance'],
    description: ['description'],
    taxRate: ['taxrate', 'tax code', 'taxtype', 'tax type'],
    enablePayments: ['enablepayments', 'enable payments to account', 'enablepaymentstoaccount'],
    status: ['status'],
  };

  const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[ _-]/g, '');
  const findImportMapping = (headers: string[]) => Object.fromEntries(Object.entries(accountixFieldAliases).map(([field, aliases]) => {
    const match = headers.find(header => aliases.some(alias => normalizeHeader(alias) === normalizeHeader(header)));
    return [field, match || ''];
  }));

  const openAccountixImport = () => {
    setImportRows([]);
    setImportHeaders([]);
    setImportMapping({});
    setImportFileName('');
    setIsAccountixImportOpen(true);
  };

  const handleAccountixFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data.filter(row => Object.values(row).some(value => String(value ?? '').trim()));
        const headers = results.meta.fields || [];
        setImportRows(rows);
        setImportHeaders(headers);
        setImportMapping(findImportMapping(headers));
      },
      error: () => notify.error('Failed to parse the CSV file.'),
    });
    event.target.value = '';
  };

  const importAccountixAccounts = async () => {
    const required = ['code', 'name', 'type'];
    if (!importRows.length || required.some(field => !importMapping[field])) {
      notify.error('Upload a CSV and map Account Code, Account Name, and Account Type first.');
      return;
    }
    setSaving(true);
    let importedCount = 0;
    let skippedCount = 0;
    try {
      for (const row of importRows) {
        const code = String(row[importMapping.code] || '').trim();
        const name = String(row[importMapping.name] || '').trim();
        if (!code || !name || accounts.some(account => account.code.toLowerCase() === code.toLowerCase())) {
          skippedCount++;
          continue;
        }
        const rawType = String(row[importMapping.type] || '').trim().toUpperCase();
        const typeMap: Record<string, Account['type']> = {
          BANK: 'Bank', CURRENT: 'Current Asset', 'CURRENT ASSET': 'Current Asset',
          FIXED: 'Fixed Asset', 'FIXED ASSET': 'Fixed Asset', NONCURRENT: 'Non-current Asset',
          EQUITY: 'Equity', DIRECTCOSTS: 'Direct Cost', 'DIRECT COST': 'Direct Cost',
          EXPENSE: 'Expense', EXPENSES: 'Expense', REVENUE: 'Sales', SALES: 'Sales',
          OTHERINCOME: 'Other Income', 'OTHER INCOME': 'Other Income',
          LIABILITY: 'Current Liability', 'CURRENT LIABILITY': 'Current Liability',
          'NON-CURRENT LIABILITY': 'Non-current Liability', NONCURRENTLIABILITY: 'Non-current Liability',
        };
        const accountType = typeMap[rawType] || 'Equity';
        const inferredNature = ['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset', 'Direct Cost', 'Expense'].includes(accountType) ? 'Debit' : 'Credit';
        const payload = {
          code, name, type: accountType,
          nature: String(row[importMapping.nature] || '').trim() || inferredNature,
          description: importMapping.description ? String(row[importMapping.description] || '').trim() : '',
          taxRate: importMapping.taxRate ? String(row[importMapping.taxRate] || '').trim() : '0%',
          enablePayments: importMapping.enablePayments ? /^true|yes|1$/i.test(String(row[importMapping.enablePayments] || '')) : accountType === 'Bank',
          status: importMapping.status && /^archived|inactive$/i.test(String(row[importMapping.status] || '')) ? 'archived' : 'active',
        };
        if (isLocalSession()) {
          await localApiRequest(`/api/organizations/${orgId}/chart-of-accounts`, { method: 'POST', body: JSON.stringify(payload) });
        } else {
          await addDoc(collection(db, 'chart_of_accounts'), { ...payload, orgId, createdBy: auth.currentUser?.uid, createdAt: serverTimestamp(), actionLog: [{ type: 'create', by: 'Accountix CSV Import', at: new Date().toISOString() }] });
        }
        importedCount++;
      }
      notify.confirm(`Accountix import complete: ${importedCount} accounts added, ${skippedCount} skipped.`);
      setIsAccountixImportOpen(false);
      if (isLocalSession()) {
        const refreshed = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts`);
        setAccounts([...localApiAccounts(refreshed)].sort((a, b) => a.code.localeCompare(b.code)));
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'An error occurred during Accountix import.');
    } finally {
      setSaving(false);
    }
  };

  const exportAccountixAccounts = () => {
    const data = filteredAccounts.map(account => ({
      'Account Code': account.code,
      'Account Name': account.name,
      'Account Type': ({ Bank: 'BANK', Sales: 'REVENUE', 'Other Income': 'OTHERINCOME', Expense: 'EXPENSE', 'Direct Cost': 'DIRECTCOSTS', Equity: 'EQUITY', 'Current Asset': 'CURRENT', 'Fixed Asset': 'FIXED', 'Non-current Asset': 'NONCURRENT', 'Current Liability': 'LIABILITY', 'Non-current Liability': 'LIABILITY' } as Record<string, string>)[account.type] || account.type.toUpperCase(),
      'Tax Code': 'NONE',
      Description: '',
      'Enable Payments To Account': account.enablePayments ? 'TRUE' : 'FALSE',
      'Show In Expense Claims': 'FALSE',
      Status: account.status === 'active' ? 'ACTIVE' : 'ARCHIVED',
      'System Account': account.isSystemAccount ? 'TRUE' : '',
      'Reporting Code': '',
      'Reporting Code Name': '',
    }));
    if (!data.length) return;
    const blob = new Blob([Papa.unparse(data)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Accountix_Chart_Of_Accounts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      localApiRequest<unknown>(`/api/organizations/${orgId}/settings`)
        .then((payload) => {
          if (!cancelled) {
            const normalized = normalizeLocalSettings(payload);
            setSettings(normalized);
            setBasicSeeded(normalized?.data?.basicChartOfAccountsSeeded === true);
          }
        })
        .catch((error) => {
          if (!cancelled) console.error('Error loading local accounting settings:', error);
        });
      return () => { cancelled = true; };
    }

    const unsub = onSnapshot(doc(db, 'settings', orgId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setSettings(data);
        setBasicSeeded(data?.data?.basicChartOfAccountsSeeded === true);
      }
    });
    return () => unsub();
  }, [orgId]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<string>('all');

  const [newAccount, setNewAccount] = useState<Partial<Account>>({
    code: '',
    name: '',
    type: 'Sales',
    nature: 'Credit',
    description: '',
    taxRate: '0%',
    enablePayments: false,
    showOnDashboardWatchlist: false,
    showInExpenseClaims: false,
    ytd: 0,
    status: 'active'
  });

  const isCodeDuplicate = newAccount.code?.trim() !== '' && 
    accounts.some(acc => 
      acc.id !== editingAccountId && 
      acc.code.trim().toLowerCase() === newAccount.code?.trim().toLowerCase()
    );

  const isFormInvalid = !newAccount.code?.trim() || !newAccount.name?.trim() || isCodeDuplicate;

  const downloadCSVTemplate = () => {
    const csv = Papa.unparse([
      { code: '1000', name: 'Cash', type: 'Bank', nature: 'Debit', description: 'Main Cash Account', taxRate: '0%' },
      { code: '4000', name: 'Sales Revenue', type: 'Sales', nature: 'Credit', description: 'Revenue from sales', taxRate: '0%' }
    ]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'coa_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSaving(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const data = results.data as any[];
        let importedCount = 0;
        let skippedCount = 0;

        try {
          for (const row of data) {
            const { code, name, type, nature, description, taxRate } = row;
            if (!code || !name) {
              skippedCount++;
              continue;
            }

            const exists = accounts.some(a => a.code === code);
            if (exists) {
              skippedCount++;
              continue;
            }

            if (isLocalSession()) {
              await localApiRequest(`/api/organizations/${orgId}/chart-of-accounts`, {
                method: 'POST',
                body: JSON.stringify({ code: code.toString(), name, type: type || 'Equity', nature: nature || 'Credit', description: description || '', taxRate: taxRate || '0%', status: 'active' })
              });
            } else {
              await addDoc(collection(db, 'chart_of_accounts'), {
                orgId,
                code: code.toString(),
                name,
                type: type || 'Equity',
                nature: nature || 'Credit',
                description: description || '',
                taxRate: taxRate || '0%',
                status: 'active',
                createdBy: auth.currentUser?.uid,
                createdAt: serverTimestamp(),
                actionLog: [{ type: 'create', by: 'CSV Import', at: new Date().toISOString() }]
              });
            }
            importedCount++;
          }
          notify.confirm(`Import complete: ${importedCount} accounts added, ${skippedCount} skipped.`);
        } catch (err) {
          console.error("Error importing CSV:", err);
          notify.error("An error occurred during import.");
        } finally {
          setSaving(false);
          if (event.target) event.target.value = '';
        }
      },
      error: (err) => {
        console.error("PapaParse error:", err);
        notify.error("Failed to parse CSV file.");
        setSaving(false);
      }
    });
  };

  const seedBasicAccounts = async () => {
    if (!orgId || (!auth.currentUser && !isLocalSession())) return;
    setSaving(true);
    if (isLocalSession()) {
      try {
        const seedResult = await localApiRequest<{ inserted: number }>(`/api/organizations/${orgId}/chart-of-accounts/seed-basic`, {
          method: 'POST',
        });
        setBasicSeeded(true);
        notify.confirm(`${seedResult.inserted} basic accounts added successfully`);
        const refreshed = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts`);
        setAccounts([...localApiAccounts(refreshed)].sort((a, b) => a.code.localeCompare(b.code)));
        return;
      } catch (error) {
        console.error('Error seeding local accounts:', error);
        notify.error(error instanceof Error ? error.message : 'Failed to import core accounts');
        return;
      } finally {
        setSaving(false);
      }
    }
    if (false && isLocalSession()) {
      try {
        const [existingPayload, localSettingsPayload, bankAccountsPayload] = await Promise.all([
          localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts`),
          localApiRequest<unknown>(`/api/organizations/${orgId}/settings`),
          localApiRequest<unknown>(`/api/organizations/${orgId}/bank-accounts`),
        ]);
        const accountsByCode = new Map(
          localApiAccounts(existingPayload).map((account) => [account.code, account])
        );
        const localSettings = normalizeLocalSettings(localSettingsPayload);
        const bankAccounts = Array.isArray(bankAccountsPayload) ? [...(bankAccountsPayload as any[])] : [];

        for (const account of CORE_CHART_OF_ACCOUNTS as any[]) {
          let chartAccount = accountsByCode.get(account.code);
          if (!chartAccount) {
            const created = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts`, {
              method: 'POST',
              body: JSON.stringify({ ...account, isSystemAccount: true, status: 'active' })
            });
            chartAccount = localApiAccount(created);
            accountsByCode.set(account.code, chartAccount);
          }

          if (account.type === 'Bank' && chartAccount?.id && !bankAccounts.some((bank: any) =>
            bank.chartAccountId === chartAccount.id || bank.accountNumber === account.code
          )) {
            const bankAccount = await localApiRequest<any>(`/api/organizations/${orgId}/bank-accounts`, {
              method: 'POST',
              body: JSON.stringify({
                accountName: account.name,
                accountNumber: account.code,
                bankName: 'System Account',
                balance: 0,
                currency: localSettings?.currency || 'USD',
                nature: account.nature,
                chartAccountId: chartAccount.id,
              })
            });
            bankAccounts.push(bankAccount);
          }
        }

        const mapping: Record<string, string> = {
          salesAccount: '4010', accountsReceivable: '1100', accountsPayable: '2010',
          inventoryAccount: '1200', cogsAccount: '5010', vatInput: '1400', vatOutput: '2100',
          vatPayable: '2110', bankCash: '1020', purchaseAccount: '5020', salesReturns: '4040',
          purchaseReturns: '5030', discountGiven: '4030', discountReceived: '6140',
        };
        const defaultAccounts = { ...(localSettings?.defaultAccounts || {}) } as Record<string, string>;
        let defaultsChanged = false;
        for (const [key, code] of Object.entries(mapping)) {
          if (!defaultAccounts[key] && accountsByCode.get(code)?.id) {
            defaultAccounts[key] = accountsByCode.get(code)!.id;
            defaultsChanged = true;
          }
        }

        if (defaultsChanged) {
          const savedSettings = await localApiRequest<unknown>(`/api/organizations/${orgId}/settings`, {
            method: 'PUT',
            body: JSON.stringify({
              defaultCurrency: localSettings?.default_currency ?? localSettings?.defaultCurrency ?? localSettings?.currency,
              defaultPaymentTerms: localSettings?.default_payment_terms ?? localSettings?.defaultPaymentTerms,
              allowedCurrencies: localSettings?.allowed_currencies ?? localSettings?.allowedCurrencies ?? [],
              defaultAccounts,
              payroll: localSettings?.payroll ?? {},
              data: localSettings?.data ?? {},
            }),
          });
          setSettings(normalizeLocalSettings(savedSettings));
        } else {
          setSettings(localSettings);
        }

        const refreshed = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts`);
        setAccounts([...localApiAccounts(refreshed)].sort((a, b) => a.code.localeCompare(b.code)));
        notify.confirm('Accounts imported and synchronized successfully');
      } catch (error) {
        console.error('Error seeding local accounts:', error);
        notify.error(error instanceof Error ? error.message : 'Failed to import core accounts');
      } finally {
        setSaving(false);
      }
      return;
    }
    const basicAccounts: Partial<Account>[] = (CORE_CHART_OF_ACCOUNTS as any[]).map(acc => ({
      ...acc,
      isSystemAccount: true,
      status: 'active' as const
    }));

    const path = 'chart_of_accounts';
    const userName = auth.currentUser.displayName || auth.currentUser.email?.split('@')[0] || 'System';
    const userEmail = auth.currentUser.email || 'system@accountex.com';

    try {
      // Fetch existing accounts to get IDs for mapping
      const existingQ = query(collection(db, path), where('orgId', '==', orgId));
      const existingSnap = await getDocs(existingQ);
      const accountsByCode: Record<string, string> = {};
      existingSnap.docs.forEach(d => {
        accountsByCode[d.data().code] = d.id;
      });

      for (const acc of basicAccounts) {
        if (!accountsByCode[acc.code]) {
          const docRef = await addDoc(collection(db, path), {
            ...acc,
            orgId,
            createdBy: auth.currentUser.uid,
            createdByName: userName,
            createdByEmail: userEmail,
            createdAt: serverTimestamp(),
            actionLog: [{ type: 'create', by: 'System Seed', at: new Date().toISOString() }]
          });
          accountsByCode[acc.code] = docRef.id;

          // If the account type is 'Bank', also create a record in bank_accounts
          if (acc.type === 'Bank') {
            const q = query(
              collection(db, 'bank_accounts'),
              where('orgId', '==', orgId),
              where('accountNumber', '==', acc.code)
            );
            const snapshot = await getDocs(q);
            
            if (snapshot.empty) {
              await addDoc(collection(db, 'bank_accounts'), {
                accountName: acc.name,
                accountNumber: acc.code,
                bankName: 'System Account',
                balance: 0,
                currency: settings?.currency || 'USD',
                nature: acc.nature,
                orgId,
                createdAt: serverTimestamp(),
                createdBy: auth.currentUser.uid,
                coaId: docRef.id
              });
            }
          }
        }
      }

      // Automatically map default accounts in settings
      const mapping: Record<string, string> = {
        'salesAccount': '4010',
        'accountsReceivable': '1100',
        'accountsPayable': '2010',
        'inventoryAccount': '1200',
        'cogsAccount': '5010',
        'vatInput': '1400',
        'vatOutput': '2100',
        'vatPayable': '2110',
        'bankCash': '1020',
        'purchaseAccount': '5020',
        'salesReturns': '4040',
        'purchaseReturns': '5030',
        'discountGiven': '4030',
        'discountReceived': '6140'
      };

      const currentDefaults = settings?.defaultAccounts || {};
      const updatedDefaultAccounts = { ...currentDefaults };
      let changed = false;

      Object.entries(mapping).forEach(([key, code]) => {
        if (!updatedDefaultAccounts[key] && accountsByCode[code]) {
          updatedDefaultAccounts[key] = accountsByCode[code];
          changed = true;
        }
      });

      if (changed) {
        await setDoc(doc(db, 'settings', orgId), {
          ...settings,
          defaultAccounts: updatedDefaultAccounts,
          orgId,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || 'system'
        }, { merge: true });
      }

      await setDoc(doc(db, 'settings', orgId), {
        ...settings,
        orgId,
        data: { ...(settings?.data || {}), basicChartOfAccountsSeeded: true, basicChartOfAccountsSeededAt: new Date().toISOString() },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setBasicSeeded(true);
      notify.confirm("Accounts imported and synchronized successfully");
    } catch (err) {
      console.error("Error seeding accounts:", err);
      notify.error("Failed to import core accounts");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setLoading(true);

      localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts`)
        .then((payload) => {
          if (!cancelled) {
            setAccounts([...localApiAccounts(payload)].sort((a, b) => a.code.localeCompare(b.code)));
            setLoading(false);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error('Error loading chart of accounts:', err);
            setError(err.message || 'Failed to load accounts.');
            setLoading(false);
          }
        });

      return () => { cancelled = true; };
    }

    const path = 'chart_of_accounts';
    const q = query(
      collection(db, path),
      where('orgId', '==', orgId),
      orderBy('code', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account));
      setAccounts(docs);
      setLoading(false);
      

    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    });

    return () => unsubscribe();
  }, [orgId]);

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!auth.currentUser && !isLocalSession()) || isFormInvalid || saving) return;

    setSaving(true);
    setError(null);
    const path = 'chart_of_accounts';
    const userName = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Unknown User';
    const userEmail = auth.currentUser?.email || 'No Email';
    
    try {
      // Infer nature based on type if not explicitly set (or always for COA form)
      const inferredNature = (['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset', 'Direct Cost', 'Expense'].includes(newAccount.type || '')) ? 'Debit' : 'Credit';
      
      if (isLocalSession()) {
        const accountPayload = { ...newAccount, nature: inferredNature };

        if (editingAccountId) {
          const response = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts/${editingAccountId}`, {
            method: 'PATCH',
            body: JSON.stringify(accountPayload),
          });
          const updatedAccount = localApiAccount(response);
          setAccounts(current => current.map(account =>
            account.id === editingAccountId ? { ...account, ...updatedAccount, ...accountPayload } as Account : account
          ));
          notify.confirm("Account updated successfully");
        } else {
          const response = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts`, {
            method: 'POST',
            body: JSON.stringify(accountPayload),
          });
          const createdAccount = localApiAccount(response);
          if (createdAccount?.id && newAccount.type === 'Bank') {
            try {
              await localApiRequest(`/api/organizations/${orgId}/bank-accounts`, {
                method: 'POST',
                body: JSON.stringify({
                  accountName: newAccount.name,
                  accountNumber: newAccount.code,
                  bankName: 'Manual Entry',
                  balance: newAccount.ytd || 0,
                  currency: settings?.currency || 'USD',
                  nature: inferredNature,
                  chartAccountId: createdAccount.id,
                }),
              });
            } catch (bankError) {
              console.error('Chart account was created but local bank profile setup failed:', bankError);
              notify.error('Account created. Complete its bank profile from Bank Accounts.');
            }
          }
          if (createdAccount?.id) {
            setAccounts(current => [...current, { ...accountPayload, ...createdAccount } as Account]
              .sort((a, b) => a.code.localeCompare(b.code)));
          }
          notify.confirm("Account created successfully");
        }
      } else if (editingAccountId) {
        const accountRef = doc(db, path, editingAccountId);
        const account = accounts.find(a => a.id === editingAccountId);
        const currentLog = account?.actionLog || [];

        await updateDoc(accountRef, {
          ...newAccount,
          nature: inferredNature,
          updatedAt: serverTimestamp(),
          actionLog: [
            ...currentLog,
            {
              type: 'update' as any,
              by: `${userName} (${userEmail})`,
              at: new Date().toISOString()
            }
          ]
        });
        notify.confirm("Account updated successfully");
      } else {
        const docRef = await addDoc(collection(db, path), {
          ...newAccount,
          nature: inferredNature,
          orgId,
          createdBy: auth.currentUser.uid,
          createdByName: userName,
          createdByEmail: userEmail,
          createdAt: serverTimestamp(),
          actionLog: [
            {
              type: 'create',
              by: `${userName} (${userEmail})`,
              at: new Date().toISOString()
            }
          ]
        });

        // If the account type is 'Bank', also create a record in bank_accounts
        if (newAccount.type === 'Bank') {
          await addDoc(collection(db, 'bank_accounts'), {
            accountName: newAccount.name,
            accountNumber: newAccount.code,
            bankName: 'Manual Entry',
            balance: newAccount.ytd || 0,
            currency: normalizeCurrency(settings?.currency ?? settings?.defaultCurrency ?? settings?.default_currency),
            nature: inferredNature,
            orgId,
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser.uid,
            coaId: docRef.id // Link back
          });
        }
        notify.confirm("Account created successfully");
      }
      setIsModalOpen(false);
      setEditingAccountId(null);
      setNewAccount({
        code: '',
        name: '',
        type: 'Sales',
        nature: 'Credit',
        description: '',
        taxRate: '0%',
        enablePayments: false,
        showOnDashboardWatchlist: false,
        showInExpenseClaims: false,
        ytd: 0,
        status: 'active'
      });
    } catch (err: any) {
      console.error("Error adding account:", err);
      setError(err.message || "Failed to save account. Please check your permissions.");
      if (!isLocalSession()) handleFirestoreError(err, OperationType.CREATE, path);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (accountId: string, newStatus: 'active' | 'archived' | 'deleted') => {
    if ((!auth.currentUser && !isLocalSession()) || saving) return;
    
    // Deletion protection checks
    if (newStatus === 'deleted') {
      const account = accounts.find(a => a.id === accountId);
      if (!account) return;

      // 1. System account check
      if (account.isSystemAccount) {
        notify.error("System accounts cannot be deleted as they are core to business processes.");
        return;
      }

      // 2. Check if linked in settings (Default Accounts)
      if (settings?.defaultAccounts) {
        const values = Object.values(settings.defaultAccounts);
        if (values.includes(accountId) || values.includes(account.code)) {
          notify.error("This account is currently set as a Default Account in Accounting Settings. Please re-assign its role before deleting.");
          return;
        }
      }

      // 3. Check for journal entries in the Firebase fallback only.
      if (!isLocalSession()) {
        setSaving(true);
        try {
          const q = query(
            collection(db, 'manual_journals'),
            where('orgId', '==', orgId)
          );
          const snapshot = await getDocs(q);
          const hasEntries = snapshot.docs.some(doc => {
            const lines = doc.data().lines || [];
            return lines.some((line: any) => line.accountId === accountId || line.accountCode === account.code);
          });

          if (hasEntries) {
            notify.error("Deletion blocked: This account has existing transactions. You should archive it instead to preserve financial history.");
            setSaving(false);
            return;
          }
        } catch (err) {
          console.error("Error checking journal entries:", err);
        }
      }
    }

    setSaving(true);
    const path = 'chart_of_accounts';
    const userName = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Unknown User';
    const userEmail = auth.currentUser?.email || 'No Email';
    const userFull = `${userName} (${userEmail})`;
    
    let actionType: 'archive' | 'delete' | 'restore' = 'archive';
    if (newStatus === 'deleted') actionType = 'delete';
    if (newStatus === 'active') actionType = 'restore';

    try {
      if (isLocalSession()) {
        if (newStatus === 'deleted') {
          const response = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts/${accountId}`, {
            method: 'DELETE',
          });
          const updatedAccount = localApiAccount(response);
          setAccounts(current => current.map(account =>
            account.id === accountId ? { ...account, ...updatedAccount, status: 'deleted' } : account
          ));
        } else {
          const response = await localApiRequest<unknown>(`/api/organizations/${orgId}/chart-of-accounts/${accountId}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: newStatus }),
          });
          const updatedAccount = localApiAccount(response);
          setAccounts(current => current.map(account =>
            account.id === accountId ? { ...account, ...updatedAccount, status: newStatus } : account
          ));
        }
        notify.confirm(`Account ${newStatus === 'deleted' ? 'deleted' : newStatus === 'archived' ? 'archived' : 'restored'} successfully`);
        return;
      }

      const accountRef = doc(db, path, accountId);
      const account = accounts.find(a => a.id === accountId);
      const currentLog = account?.actionLog || [];
      
      await updateDoc(accountRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        lastActionBy: userFull,
        lastActionAt: serverTimestamp(),
        actionLog: [
          ...currentLog,
          {
            type: actionType,
            by: userFull,
            at: new Date().toISOString()
          }
        ]
      });
      notify.confirm(`Account ${newStatus === 'deleted' ? 'deleted' : newStatus === 'archived' ? 'archived' : 'restored'} successfully`);
    } catch (err: any) {
      console.error("Error updating account status:", err);
      setError(err.message || "Failed to update account status.");
      if (!isLocalSession()) handleFirestoreError(err, OperationType.UPDATE, path);
    } finally {
      setSaving(false);
    }
  };

  const filteredAccounts = accounts.filter(acc => {
    // Filter by status first
    if (filter === 'archived') {
      if (acc.status !== 'archived') return false;
    } else if (filter === 'deleted') {
      if (acc.status !== 'deleted') return false;
    } else {
      // For 'all' and categories, only show active accounts
      if (acc.status !== 'active') return false;
      
      if (filter !== 'all') {
        const matchesCategory = Object.entries(ACCOUNT_TYPES).some(([category, types]) => 
          category === filter && types.includes(acc.type as any)
        );
        if (!matchesCategory) return false;
      }
    }

    const matchesSearch = acc.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         acc.code.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Chart of Accounts</h1>
            <p className="text-sm opacity-50 italic serif mt-1">Manage your ledger accounts and financial structure</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openAccountixImport}
              className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-3 rounded-full hover:opacity-90 transition-all font-bold text-xs"
              title="Import accounts using an Accountix CSV template"
            >
              <Link2 className="w-4 h-4" />
              <span>Import</span>
            </button>
            {!basicSeeded && (
              <button
                onClick={seedBasicAccounts}
                disabled={saving}
                className="flex items-center gap-2 bg-white border border-[#141414] text-[#141414] px-6 py-3 rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold text-sm disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusSquare className="w-4 h-4" />}
                <span>Seed Basic</span>
              </button>
            )}
            <button 
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
            >
              <Plus className="w-4 h-4" />
              <span>{language === 'ar' ? 'إضافة حساب' : 'Add Account'}</span>
            </button>
          </div>
        </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
          {['all', ...Object.keys(ACCOUNT_TYPES), 'archived', 'deleted'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "px-4 py-2 rounded-full text-xs font-bold capitalize transition-all whitespace-nowrap shrink-0",
                filter === f ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5 opacity-60"
              )}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full flex-1 md:w-64">
            <Search className="w-4 h-4 opacity-50" />
            <input 
              type="text" 
              placeholder="Search accounts..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-full"
            />
          </div>
        </div>
      </div>

      {/* Accounts List */}
      <div className="bg-white/50 border border-[#141414] rounded-2xl">
        <div className="overflow-x-auto rounded-2xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#141414]">
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Code</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Name</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'النوع' : 'Type'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif text-right">YTD</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-20" />
                  </td>
                </tr>
              ) : filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-12 text-center opacity-50 italic serif">
                    No accounts found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredAccounts.map((acc, index) => (
                  <tr 
                    key={acc.id} 
                    onClick={() => setSelectedAccount(acc)}
                    className="border-b border-[#141414]/10 hover:bg-[#141414] hover:text-[#E4E3E0] transition-all group cursor-pointer"
                  >
                    <td className="p-4 font-mono text-sm font-bold">{acc.code}</td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <button 
                        onClick={() => {
                          if (onNavigate) {
                            onNavigate('reports', 'Account Transactions', { accountId: acc.id });
                          }
                        }}
                        className="text-sm font-bold hover:underline hover:text-white transition-colors text-left"
                      >
                        {acc.name}
                      </button>
                    </td>
                    <td className="p-4">
                      <span className={cn(
                        "flex items-center w-fit gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
                        ACCOUNT_TYPES.Revenue.includes(acc.type as any) ? "bg-green-100 text-green-600" : 
                        ACCOUNT_TYPES.Expenses.includes(acc.type as any) ? "bg-red-100 text-red-600" :
                        ACCOUNT_TYPES.Assets.includes(acc.type as any) ? "bg-blue-100 text-blue-600" :
                        ACCOUNT_TYPES.Liability.includes(acc.type as any) ? "bg-orange-100 text-orange-600" :
                        "bg-purple-100 text-purple-600",
                        "group-hover:bg-white/20 group-hover:text-white"
                      )}>
                        {ACCOUNT_TYPES.Revenue.includes(acc.type as any) ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                        {acc.type}
                      </span>
                    </td>
                    <td className="p-4 font-mono font-bold text-sm text-right">
                      ${(acc.ytd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="p-4 relative">
                      {filter === 'deleted' ? (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(acc.id, 'active');
                          }}
                          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-full hover:opacity-90 transition-opacity text-xs font-bold whitespace-nowrap"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Restore</span>
                        </button>
                      ) : (
                        <>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeMenu !== acc.id) announceDropdownOpen();
                              setActiveMenu(activeMenu === acc.id ? null : acc.id);
                            }}
                            className="p-2 hover:bg-white/20 rounded-full transition-colors"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>

                          {activeMenu === acc.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setActiveMenu(null)}
                              />
                              <div className={cn(
                                "absolute right-0 w-48 bg-[#E4E3E0] border border-[#141414] text-[#141414] rounded-2xl shadow-xl z-50 overflow-hidden",
                                index === 0 ? "top-full mt-2" : "bottom-full mb-2"
                              )}>
                                {acc.actionLog && acc.actionLog.length > 0 && (
                                  <div className="px-4 py-2 bg-[#141414]/5 border-b border-[#141414]/10 max-h-32 overflow-y-auto custom-scrollbar">
                                    <p className="text-[10px] uppercase tracking-widest opacity-50 font-bold mb-1">Action History</p>
                                    {acc.actionLog.slice(-2).reverse().map((log, i) => (
                                      <div key={i} className="mb-2 last:mb-0">
                                        <p className="text-[10px] font-bold capitalize">{`${log.type === 'create' ? 'Created' : log.type === 'restore' ? 'Restored' : log.type === 'update' ? 'Updated' : `${log.type}d`} by:`}</p>
                                        <p className="text-[10px] opacity-70 truncate">{log.by}</p>
                                        <p className="text-[8px] opacity-40">{new Date(log.at).toLocaleDateString()} {new Date(log.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingAccountId(acc.id);
                                    setNewAccount({
                                      code: acc.code,
                                      name: acc.name,
                                      type: acc.type,
                                      nature: acc.nature || (['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset', 'Direct Cost', 'Expense'].includes(acc.type) ? 'Debit' : 'Credit'),
                                      description: acc.description || '',
                                      taxRate: acc.taxRate || '0%',
                                      enablePayments: acc.enablePayments || false,
                                      showOnDashboardWatchlist: acc.showOnDashboardWatchlist || false,
                                      showInExpenseClaims: acc.showInExpenseClaims || false,
                                      ytd: acc.ytd ?? 0,
                                      status: acc.status
                                    });
                                    setIsModalOpen(true);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-colors"
                                >
                                  <Edit2 className="w-4 h-4" />{language === 'ar' ? 'تعديل الحساب' : 'Edit Account'}</button>

                                {acc.status === 'active' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(acc.id, 'archived');
                                      setActiveMenu(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-colors"
                                  >
                                    <Archive className="w-4 h-4" />
                                    Archive
                                  </button>
                                )}
                                
                                {(acc.status === 'archived' || acc.status === 'deleted') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(acc.id, 'active');
                                      setActiveMenu(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-colors"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                    Restore to Active
                                  </button>
                                )}

                                {acc.status !== 'deleted' && !acc.isSystemAccount && (
                                  <button
                                    disabled={(acc.ytd || 0) !== 0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if ((acc.ytd || 0) === 0) {
                                        handleStatusChange(acc.id, 'deleted');
                                        setActiveMenu(null);
                                      }
                                    }}
                                    className={cn(
                                      "w-full flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors",
                                      (acc.ytd || 0) === 0 
                                        ? "text-red-600 hover:bg-red-600 hover:text-white" 
                                        : "opacity-30 cursor-not-allowed"
                                    )}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    <div className="flex flex-col items-start">
                                      <span>Delete</span>
                                      {(acc.ytd || 0) !== 0 && (
                                        <span className="text-[8px] uppercase tracking-tighter">Only for 0 balance</span>
                                      )}
                                    </div>
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isAccountixImportOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-[#141414]/40 backdrop-blur-sm" onMouseDown={(event) => { if (event.target === event.currentTarget) setIsAccountixImportOpen(false); }}>
          <div className="bg-white w-full max-w-3xl rounded-3xl border border-[#141414] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-6 bg-[#141414] text-[#E4E3E0] flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold">Import Accounts</h2>
                <p className="text-sm opacity-70 mt-1">Load the template, complete your account information, then upload the CSV and match its columns.</p>
              </div>
              <button onClick={() => setIsAccountixImportOpen(false)} className="p-2 hover:bg-white/10 rounded-full"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto">
              <div className="grid md:grid-cols-2 gap-4">
                <a href={accountixTemplateUrl} download="accountix_accounts_template.csv" className="border border-[#141414] rounded-2xl p-5 flex items-start gap-3 hover:bg-[#141414]/5">
                  <FileDown className="w-5 h-5 shrink-0" />
                  <span><strong className="block">Load Template</strong><small className="opacity-60">Download the Accountix account CSV template</small></span>
                </a>
                <label className="border border-[#141414] rounded-2xl p-5 flex items-start gap-3 hover:bg-[#141414]/5 cursor-pointer">
                  <Upload className="w-5 h-5 shrink-0" />
                  <span><strong className="block">Upload CSV</strong><small className="opacity-60">{importFileName || 'Choose your completed Accountix CSV file'}</small></span>
                  <input type="file" accept=".csv,text/csv" onChange={handleAccountixFile} className="hidden" />
                </label>
              </div>
              {importHeaders.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between"><h3 className="font-bold">Match columns</h3><span className="text-xs opacity-60">{importRows.length} rows detected</span></div>
                  <p className="text-sm opacity-60">Choose which uploaded column supplies each Accountix field. Required fields are marked *.</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {Object.entries(accountixFieldAliases).map(([field]) => (
                      <label key={field} className="text-xs font-bold uppercase tracking-wider">
                        {field === 'code' ? 'Account Code *' : field === 'name' ? 'Account Name *' : field === 'type' ? 'Account Type *' : field}
                        <select value={importMapping[field] || ''} onChange={(event) => setImportMapping(current => ({ ...current, [field]: event.target.value }))} className="mt-1 w-full border border-[#141414]/30 rounded-xl px-3 py-2 bg-white font-normal normal-case tracking-normal">
                          <option value="">Do not import</option>
                          {importHeaders.map(header => <option key={header} value={header}>{header}</option>)}
                        </select>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-[#141414]/10 flex justify-end gap-3">
              <button onClick={() => setIsAccountixImportOpen(false)} className="px-5 py-2 rounded-full border border-[#141414] font-bold text-sm">Cancel</button>
              <button onClick={exportAccountixAccounts} className="px-5 py-2 rounded-full border border-[#141414] font-bold text-sm flex items-center gap-2"><Download className="w-4 h-4" />Export CSV</button>
              <button onClick={importAccountixAccounts} disabled={saving || !importHeaders.length} className="px-5 py-2 rounded-full bg-[#141414] text-[#E4E3E0] font-bold text-sm disabled:opacity-50 flex items-center gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Import matched accounts</button>
            </div>
          </div>
        </div>
      )}

      {/* Account Details Modal */}
      {selectedAccount && (
        <div
                  className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
                  onMouseDown={(event) => {
                    if (event.target === event.currentTarget) setSelectedAccount(null);
                  }}
                >
          <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-900 text-white dark:bg-slate-950 dark:border-slate-800">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center font-bold text-xl">
                  {selectedAccount.code}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedAccount.name}</h2>
                  <p className="text-xs opacity-70 uppercase tracking-widest">{selectedAccount.type}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedAccount(null)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {/* Basic Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-widest opacity-50 font-bold">YTD Balance</p>
                  <p className="font-mono font-bold text-lg text-[#141414]">
                    ${(selectedAccount.ytd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-widest opacity-50 font-bold">Status</p>
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tighter w-fit block",
                    selectedAccount.status === 'active' ? "bg-green-100 text-green-700" :
                    selectedAccount.status === 'archived' ? "bg-orange-100 text-orange-700" :
                    "bg-red-100 text-red-700"
                  )}>
                    {selectedAccount.status}
                  </span>
                </div>
              </div>

              {/* Action History */}
              <div className="space-y-4 pt-4 border-t border-[#141414]/10 text-[#141414]">
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 opacity-50" />
                  <h3 className="text-xs uppercase tracking-[0.2em] font-bold opacity-50">Action History</h3>
                </div>
                
                <div className="space-y-4">
                  {/* Combined History: Action Log + Creation Info */}
                  {(() => {
                    const history = [...(selectedAccount.actionLog || [])];
                    
                    // If 'create' isn't in the log (for older accounts), add it manually from createdAt
                    if (!history.some(log => log.type === 'create') && selectedAccount.createdAt) {
                      history.unshift({
                        type: 'create' as const,
                        by: selectedAccount.createdByName 
                          ? `${selectedAccount.createdByName} (${selectedAccount.createdByEmail || 'No Email'})`
                          : 'Original Creator',
                        at: selectedAccount.createdAt.seconds 
                          ? new Date(selectedAccount.createdAt.seconds * 1000).toISOString()
                          : selectedAccount.createdAt
                      });
                    }

                    return history.slice().reverse().map((log, i) => (
                      <div key={i} className="relative pl-6 pb-4 border-l border-[#141414]/10 last:pb-0">
                        <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-[#141414]" />
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-[10px] font-bold uppercase px-2 py-0.5 rounded-md",
                              log.type === 'create' ? "bg-green-100 text-green-700" :
                              log.type === 'restore' ? "bg-blue-100 text-blue-700" :
                              log.type === 'archive' ? "bg-orange-100 text-orange-700" :
                              "bg-red-100 text-red-700"
                            )}>
                              {log.type === 'create' ? 'Created' : log.type === 'restore' ? 'Restored' : log.type + 'd'}
                            </span>
                            <span className="text-[10px] opacity-40 font-mono">
                              {log.at ? (
                                <>
                                  {new Date(log.at).toLocaleDateString()} {new Date(log.at).toLocaleTimeString()}
                                </>
                              ) : 'Unknown Time'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <User className="w-3 h-3 opacity-30" />
                            <span className="opacity-70 font-bold">{log.by}</span>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                  
                  {(!selectedAccount.actionLog || selectedAccount.actionLog.length === 0) && !selectedAccount.createdAt && (
                    <p className="text-xs italic opacity-40">No history recorded yet.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 bg-[#141414]/5 border-t border-[#141414] flex justify-end gap-3">
              {selectedAccount.status !== 'active' && (
                <button 
                  onClick={() => {
                    handleStatusChange(selectedAccount.id, 'active');
                    setSelectedAccount(null);
                  }}
                  className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold text-sm"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Restore Account
                </button>
              )}
              <button 
                onClick={() => setSelectedAccount(null)}
                className="px-6 py-3 rounded-full border border-[#141414] font-bold text-sm hover:bg-[#141414] hover:text-[#E4E3E0] transition-all text-[#141414]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Account Modal */}
      {isModalOpen && (
        <div
                  className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
                  onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                      setIsModalOpen(false);
                      setEditingAccountId(null);
                    }
                  }}
                >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-6xl rounded-none overflow-hidden shadow-2xl flex flex-col md:flex-row max-h-[94vh]">
            {/* Form Side */}
            <div className="flex-1 p-7 sm:p-8 overflow-y-auto custom-scrollbar border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-8 border-b border-slate-200 dark:border-slate-700 pb-5">
                <h2 className="text-3xl font-bold tracking-tight text-slate-800 dark:text-white">{editingAccountId ? 'Edit Account' : 'Add New Account'}</h2>
                <button aria-label="Close" onClick={() => {
                  setIsModalOpen(false);
                  setEditingAccountId(null);
                  setNewAccount({
                    code: '',
                    name: '',
                    type: 'Sales',
                    nature: 'Credit',
                    enablePayments: false,
                    showOnDashboardWatchlist: false,
                    ytd: 0,
                    status: 'active'
                  });
                }} className="md:hidden p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-xl text-sm font-bold mb-6 flex items-center gap-2">
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleAddAccount} className="space-y-6">
                <div className="space-y-5">
                  <div>
                    <label className="block text-base font-bold mb-2 text-slate-900 dark:text-white">Account Type *</label>
                    <select required value={newAccount.type} onChange={(e) => setNewAccount(prev => ({ ...prev, type: e.target.value as any }))} className="w-full sm:w-80 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-3 outline-none focus:border-primary">
                      {Object.entries(ACCOUNT_TYPES).map(([category, types]) => <optgroup key={category} label={category}>{types.map(type => <option key={type} value={type}>{type}</option>)}</optgroup>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-base font-bold mb-1 text-slate-900 dark:text-white">Code *</label>
                    <p className="text-sm text-muted-foreground mb-2">A unique code or number for this account</p>
                    <input type="text" required placeholder="e.g. 200" value={newAccount.code} onChange={(e) => setNewAccount(prev => ({ ...prev, code: e.target.value }))} className={cn("w-full sm:w-72 bg-white dark:bg-slate-800 border p-3 outline-none transition-colors", isCodeDuplicate ? "border-red-500" : "border-slate-300 dark:border-slate-600 focus:border-primary")} />
                    {isCodeDuplicate && <p className="text-xs text-red-500 mt-1 font-bold">This code is already in use.</p>}
                  </div>
                  <div>
                    <label className="block text-base font-bold mb-1 text-slate-900 dark:text-white">Name *</label>
                    <p className="text-sm text-muted-foreground mb-2">A short title for this account</p>
                    <input type="text" required placeholder="e.g. Sales" value={newAccount.name} onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-3 outline-none focus:border-primary" />
                  </div>
                  <div>
                    <label className="block text-base font-bold mb-1 text-slate-900 dark:text-white">Description <span className="font-normal">(optional)</span></label>
                    <p className="text-sm text-muted-foreground mb-2">A description of how this account should be used</p>
                    <textarea value={newAccount.description || ''} onChange={(e) => setNewAccount(prev => ({ ...prev, description: e.target.value }))} className="w-full min-h-20 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-3 outline-none focus:border-primary" />
                  </div>
                  <div>
                    <label className="block text-base font-bold mb-1 text-slate-900 dark:text-white">Tax</label>
                    <p className="text-sm text-muted-foreground mb-2">The default tax setting for this account</p>
                    <select value={newAccount.taxRate || '0%'} onChange={(e) => setNewAccount(prev => ({ ...prev, taxRate: e.target.value }))} className="w-full sm:w-72 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-3 outline-none focus:border-primary"><option value="0%">No Tax</option><option value="5%">5%</option><option value="15%">15%</option><option value="20%">20%</option></select>
                  </div>
                  <div className="flex items-center gap-3 py-1"><input type="checkbox" id="showOnDashboardWatchlist" checked={newAccount.showOnDashboardWatchlist || false} onChange={(e) => setNewAccount(prev => ({ ...prev, showOnDashboardWatchlist: e.target.checked }))} className="w-5 h-5" /><label htmlFor="showOnDashboardWatchlist" className="text-base cursor-pointer">Show on Dashboard Watchlist</label></div>
                  <div className="flex items-center gap-3 py-1"><input type="checkbox" id="showInExpenseClaims" checked={newAccount.showInExpenseClaims || false} onChange={(e) => setNewAccount(prev => ({ ...prev, showInExpenseClaims: e.target.checked }))} className="w-5 h-5" /><label htmlFor="showInExpenseClaims" className="text-base cursor-pointer">Show in Expense Claims</label></div>
                  <div className="flex items-center gap-3 py-1"><input type="checkbox" id="enablePayments" checked={newAccount.enablePayments || false} onChange={(e) => setNewAccount(prev => ({ ...prev, enablePayments: e.target.checked }))} className="w-5 h-5" /><label htmlFor="enablePayments" className="text-base cursor-pointer">Enable payments to this account</label></div>
                </div>

                <div className="flex gap-4 pt-4 border-t border-primary/10">
                  <button 
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingAccountId(null);
                      setNewAccount({
                        code: '',
                        name: '',
                        type: 'Sales',
                        nature: 'Credit',
                        description: '',
                        taxRate: '0%',
                        enablePayments: false,
                        showOnDashboardWatchlist: false,
                        showInExpenseClaims: false,
                        ytd: 0,
                        status: 'active'
                      });
                    }}
                    className="flex-1 py-3 rounded border border-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                  >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                  <button 
                    type="submit"
                    disabled={isFormInvalid || saving}
                    className={cn(
                      "flex-1 py-3 rounded-full font-bold transition-all flex items-center justify-center gap-2",
                      (isFormInvalid || saving)
                        ? "bg-muted text-muted-foreground cursor-not-allowed" 
                        : "bg-primary text-primary-foreground hover:opacity-90 shadow-lg shadow-primary/10"
                    )}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>{editingAccountId ? 'Update Account' : 'Save'}</span>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Guide Side */}
            <div className="w-full md:w-[42%] bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 p-8 overflow-y-auto custom-scrollbar">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold">How account types affect your reports</h3>
                <button onClick={() => setIsModalOpen(false)} className="hidden md:block p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-all text-slate-500">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-8 mt-8">
                {/* Profit & Loss Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 border-b border-white/20 pb-2">
                    <BookOpen className="w-4 h-4" />
                    <h4 className="font-bold text-sm">Profit & Loss</h4>
                  </div>
                  <div className="space-y-3 text-xs">
                    <div className="space-y-1">
                      <p className="opacity-50 font-bold uppercase tracking-tighter text-[9px]">Income</p>
                      <ul className="list-disc list-inside opacity-80">
                        <li>Revenue</li>
                        <li>Sales</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="opacity-50 font-bold uppercase tracking-tighter text-[9px]">Less Cost of Sales</p>
                      <ul className="list-disc list-inside opacity-80">
                        <li>Direct Costs</li>
                      </ul>
                    </div>
                    <div className="py-1 border-y border-white/10 font-bold text-[10px] uppercase tracking-widest">Gross Profit</div>
                    <div className="space-y-1">
                      <p className="opacity-50 font-bold uppercase tracking-tighter text-[9px]">Plus Other Income</p>
                      <ul className="list-disc list-inside opacity-80">
                        <li>Other Income</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="opacity-50 font-bold uppercase tracking-tighter text-[9px]">Less Expenses</p>
                      <ul className="list-disc list-inside opacity-80">
                        <li>Expenses</li>
                        <li>Depreciation</li>
                        <li>Overheads</li>
                      </ul>
                    </div>
                    <div className="py-2 border-t-2 border-white font-bold text-xs uppercase tracking-widest flex justify-between">
                      <span>Net Profit</span>
                      <ArrowUpRight className="w-3 h-3 animate-pulse" />
                    </div>
                  </div>
                </div>

                {/* Balance Sheet Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 border-b border-white/20 pb-2">
                    <ArrowUpRight className="w-4 h-4" />
                    <h4 className="font-bold text-sm">Balance Sheet</h4>
                  </div>
                  <div className="space-y-3 text-xs">
                    <div className="space-y-1">
                      <p className="opacity-50 font-bold uppercase tracking-tighter text-[9px]">Assets</p>
                      <ul className="list-disc list-inside opacity-80">
                        <li>Current Assets</li>
                        <li>Bank Accounts</li>
                        <li>Fixed Assets</li>
                        <li>Non-current Assets</li>
                      </ul>
                    </div>
                    <div className="py-1 border-y border-white/10 font-bold text-[10px] uppercase tracking-widest">Total Assets</div>
                    <div className="space-y-1">
                      <p className="opacity-50 font-bold uppercase tracking-tighter text-[9px]">Less Liabilities</p>
                      <ul className="list-disc list-inside opacity-80">
                        <li>Current Liabilities</li>
                        <li>Non-current Liabilities</li>
                      </ul>
                    </div>
                    <div className="py-1 border-y border-white/10 font-bold text-[10px] uppercase tracking-widest">Net Assets</div>
                    <div className="space-y-1">
                      <p className="opacity-50 font-bold uppercase tracking-tighter text-[9px]">Equity</p>
                      <ul className="list-disc list-inside opacity-80">
                        <li>Equity</li>
                        <li className="text-green-400 font-bold">Plus Net Profit</li>
                      </ul>
                    </div>
                    <div className="py-2 border-t-2 border-white font-bold text-xs uppercase tracking-widest">Total Equity</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-white/5 rounded-2xl border border-white/10">
                <p className="text-[10px] leading-relaxed opacity-60 italic">
                  Net Profit from your Profit & Loss report automatically flows into the Equity section of your Balance Sheet.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
